<script>
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Icon from '$lib/Components/Layout/Icon/Icon.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';

	import TLCurve from './images/IW-Transmission_Loss_Curve.svg';

	import building1Image from './images/building1.png';
	import building2Image from './images/building2.png';
	import crossSectionImagge from './images/IndowPRO-2.0-Cross_Section-tape-callouts.svg';

	const performanceDataHeaders = [
		'IndowPRO Glazing Insert',
		'Air Gap',
		'Resulting STC',
		'Resulting OITC',
		'Test Report #',
	];

	const performanceDataTable = [
		[
			{ value: `None (Primary Window Only)`, bold: false },
			{ value: `N/A`, bold: false, italic: true },
			{ value: `29`, bold: true },
			{ value: `25*`, bold: false },
			{ value: 'NOAL 25-06063', bold: false },
		],
		[
			{ value: `5/16" Acoustic Laminated`, bold: false },
			{ value: `1.25"`, bold: false },
			{ value: `43`, bold: true },
			{ value: `31`, bold: false },
			{ value: 'NOAL 25-06065', bold: false },
		],
		[
			{ value: `5/16" Acoustic Laminated`, bold: true },
			{ value: `4.00"`, bold: true },
			{ value: `51`, bold: true },
			{ value: `36`, bold: true },
			{ value: 'NOAL 25-06069', bold: true },
		],
		[],
	];
</script>

<Section>
	<p class="text-left">
		Engineer superior acoustic environments with the IndowPRO secondary glazing
		system. Achieve STC ratings up to 51 and OITC ratings up to 36 over a
		standard storefront window, with performance validated by accredited,
		third-party ASTM E90 lab testing.
	</p>

	<Button class="mb-8" href="#files">View Full Acoustic Test Reports</Button>

	<h2 class="text-left">Lab-Verified STC 51 & OITC 36 Performance</h2>
	<p class="text-left">
		Solving complex acoustic challenges in existing buildings requires a systems
		approach that moves beyond the limitations of the primary window. IndowPRO
		creates a predictable, high-performance assembly by introducing a
		significant, decoupled air gap and optimizing the mass of acoustic-grade
		glazing, all within a verifiably airtight frame system (&lt;0.01 CFM).
	</p>
	<p class="text-left">
		The IndowPRO system is a secondary glazing solution that supports customer
		glazing selection to meet specific project goals. The following lab-tested
		results demonstrate the acoustic performance of IndowPRO with various
		glazing types and air gaps when installed over a baseline commercial
		aluminum storefront window (STC 29).
	</p>

	<div
		class="
    mb-3 w-full max-w-lg
    md:max-w-3xl
  "
	>
		<h2 class="mb-2 text-lg font-semibold">
			Primary Window: 1/4" Tempered Glass (STC 29)
		</h2>

		<div class="overflow-x-auto">
			<table class="min-w-full border-collapse text-sm">
				<thead
					class="
       hidden bg-indow-blue/50
       md:table-header-group
     "
				>
					<tr>
						{#each performanceDataHeaders as h, i (i)}
							<th class="p-2 text-left font-medium">{h}</th>
						{/each}
					</tr>
				</thead>

				<tbody>
					{#each performanceDataTable as row, r (r)}
						<tr
							class="
         block border-b border-indow-grey-light pb-6
         md:table-row md:border-0 md:pb-0
         md:even:bg-gray-50
       "
						>
							{#each row as { value, bold, italic }, c (c)}
								<td
									class="
           flex justify-between border-gray-200 py-2
           md:table-cell md:border md:p-3
         "
								>
									<span
										class="
            font-medium
            md:hidden
          "
										>{performanceDataHeaders[c]}:
									</span>
									<span
										class={[
											`
             text-right
             md:text-left
           `,
											{ italic: italic },
											{ 'font-bold': bold },
										]}>{value}</span
									>
								</td>
							{/each}
						</tr>
					{/each}
				</tbody>
			</table>
		</div>
	</div>
	<p class="text-sm">* OITC estimated for baseline</p>

	<Image src={TLCurve} class="w-full max-w-3xl!" />

	<h2 class="text-left">Secondary Glazing, Redefined</h2>

	<Image src={crossSectionImagge} />

	<p class="text-left">
		The modular frame ships disassembled to reduce logistics costs, then snaps
		together quickly on-site for fast, non-disruptive installation.
	</p>
</Section>

<Section>
	<div
		class="
    rounded-lg bg-indow-blue p-6 shadow-md
    **:text-left
  "
	>
		<h3>IndowPRO Glazing System</h3>
		<p class="text-indow-grey-dark">
			Learn more about <a href="/solutions/commercial-window-inserts"
				>IndowPRO for commercial projects</a
			>. This secondary glazing system provides high performance window upgrades
			without the need for demolition or traditional replacement.
		</p>
	</div>
</Section>

<Section backgroundColor="grey-light">
	<h2 class="mb-8 text-indow-grey-dark">
		Solving Real-World Acoustic Challenges
	</h2>

	<ul
		class="
    mb-12 grid max-w-3xl list-none gap-12
    md:grid-cols-2
  "
	>
		<li>
			<Image src={building1Image} class="mb-6 h-80 w-sm" />
			<p class="text-lg font-bold">Hospitality/Transportation Noise</p>
			<p class="m-0 font-medium">Challenge:</p>
			<p>Mitigate street noise to meet premium brand standards.</p>
			<p class="m-0 font-medium">Solution:</p>
			<p>
				IndowPRO with 4" air gap and laminated glass achieved STC 51, creating a
				serene guest environment.
			</p>
		</li>
		<li>
			<Image src={building2Image} class="mb-6 h-80 w-sm" />
			<p class="text-lg font-bold">Atrium Window Noise Damping</p>
			<p class="m-0 font-medium">Challenge:</p>
			<p>Damping atrium event noise from reaching guest rooms</p>
			<p class="m-0 font-medium">Solution:</p>
			<p>IndowPRO with Acoustic Laminated Glass in mullion array.</p>
		</li>
	</ul>
</Section>

<Section backgroundColor="blue">
	<h2 class="mb-8">Let's Solve Your Window Challenge</h2>
	<Button href="/about-indow/commercial-sales">Request a Consultation</Button>
</Section>

<Section backgroundColor="grey-dark" id="files">
	<h2 class="text-left">Your Complete Technical & Specification Library</h2>
	<p class="text-left">
		Access the documentation needed to model, specify, and present the IndowPRO
		system.
	</p>
	<Button
		color="blue"
		class="mb-8 place-self-start"
		href="/static/routes/how-to/solve-acoustic-issues-in-commercial-buildings/IndowPRO_Technical_Library.zip"
		>Download All (.zip)</Button
	>

	<ul
		class="
    grid list-none gap-6 text-indow-blue
    md:grid-cols-2
  "
	>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				href="/static/routes/how-to/solve-acoustic-issues-in-commercial-buildings/NOAL_25-06069_test_report.pdf"
				target="_blank">STC 51 Assembly (NOAL 25-06069) (.pdf)</a
			>
		</li>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				href="/static/routes/how-to/solve-acoustic-issues-in-commercial-buildings/NOAL_25-06065_test_report.pdf"
				target="_blank">STC 43 Assembly (NOAL 25-06065) (.pdf)</a
			>
		</li>
	</ul>

	<ul
		class="
    grid list-none gap-6 text-indow-blue
    md:grid-cols-2
  "
	>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				target="_blank"
				href="/static/routes/how-to/solve-acoustic-issues-in-commercial-buildings/NOAL_IND-L-0002_preliminary_result_conparison.xlsx"
				>Transmission Loss Data (.xlsx)</a
			>
		</li>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				target="_blank"
				href="/static/routes/how-to/solve-acoustic-issues-in-commercial-buildings/IndowPRO_CSI_Spec_-_Section_08_35_13.13_-_Q4_25.pdf"
				>CSI 3-Part Specification (.pdf)</a
			>
		</li>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				target="_blank"
				href="/static/routes/how-to/solve-acoustic-issues-in-commercial-buildings/IndowPRO_CSI_Specification_Document_-_Section_08_35_13.13_-_Q4_25.docx"
				>CSI 3-Part Specification (.docx)</a
			>
		</li>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				target="_blank"
				href="/static/routes/how-to/solve-acoustic-issues-in-commercial-buildings/IW-IndowPRO-Product_Details-Sheet-11-11-25.pdf"
				>Technical Detail Drawings (.pdf)</a
			>
		</li>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				target="_blank"
				href="/static/routes/how-to/solve-acoustic-issues-in-commercial-buildings/IW-IndowPRO-Product_Data-Sheet-08-10-25.pdf"
				>Product Data Sheet (.pdf)</a
			>
		</li>
	</ul>
</Section>

<Section>
	<h2 class="text-left">Project Support</h2>
	<p class="text-left">
		Our team is available to aid your analysis and planning. We can discuss
		glazing options and partner with you to engineer a solution that meets your
		client's performance goals.
	</p>

	<p
		class="
    grid text-left
    sm:grid-cols-2 sm:gap-6 sm:text-center
    md:grid-cols-4
  "
	>
		<a href="/solutions/commercial-window-inserts">About IndowPRO</a>
		<a href="/how-to/install-indowpro">Installation</a>
		<a href="/how-to/indowpro-for-architects">Architectural Details</a>
		<a href="/how-to/commercial-noi">Performance </a>
	</p>

	<Button href="/about-indow/commercial-sales">Request a Project Review</Button>
</Section>
